import styles from './button.module.css'
import { useEffect, useRef } from 'react'
import { ButtonKind } from './types'
import { useNavigate } from 'react-router-dom'

type ButtonType = 'submit' | 'reset' | 'button'

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    label?: string
    kind?: ButtonKind
    type?: ButtonType
    icon?: string
    href?: string
    rightMargin?: boolean
    disabled?: boolean
    className?: string
    small?: boolean
    tooltip?: string
    tooltipLines?: string[]
    round?: boolean
    focus?: boolean
    focusable?: boolean
    onClick?: () => void
}

export const Button = (props: Props) => {
    const { label, kind, type, icon, href, rightMargin, disabled, className, small, tooltip, tooltipLines, round, focus, focusable = true, onClick, ...rest } = props

    const navigate = useNavigate()
    const inputRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        focus && inputRef && inputRef.current?.focus()
    }, [focus])

    const rightMarginClass = small ? styles.smallRightMargin : styles.rightMargin
    const marginClass = rightMargin ? rightMarginClass : ''
    const mainClass = small ? styles.small : styles.btn
    const roundClass = round ? styles.round : ''

    const getKindClass = () => {
        switch(kind) {
            case 'naked':
                return styles.btnNaked
            case 'senary':
                return styles.btnSenary
            case 'quinary':
                return styles.btnQuinary
            case 'quaternary':
                return styles.btnQuaternary
            case 'tertiary':
                return styles.btnTertiary
            case 'secondary':
                return styles.btnSecondary
            case 'primary':
            default:
                return styles.btnPrimary
        }
    }
    const labelClass = label ? (small ? styles.smallRightMargin : styles.rightMargin) : ''
    const propClass = className || ''

    const tooltipValue = tooltipLines ?
        tooltipLines.map((line, index) => <div key={index}>{line} <br /></div>) :
        tooltip

    const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (disabled) {
            e.preventDefault()
            return
        }
        if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
            e.preventDefault()
            navigate(href!)
        }
    }

    const buttonContent = (
        <>
            {icon && <i className={`bi bi-${icon} ${labelClass}`}></i>}
            {label}
        </>
    )

    return (
        <div className={`${styles.container} ${marginClass}`}>
            {href ? (
                <a
                    href={href}
                    onClick={handleAnchorClick}
                    className={`btn ${mainClass} ${getKindClass()} ${propClass} ${roundClass} ${disabled ? 'disabled' : ''}`}
                    aria-disabled={disabled}
                    tabIndex={focusable ? undefined : -1}
                >
                    {buttonContent}
                </a>
            ) : (
                <button
                    type={type || 'button'}
                    onClick={onClick}
                    className={`btn ${mainClass} ${getKindClass()} ${propClass} ${roundClass}`}
                    disabled={disabled}
                    tabIndex={focusable ? 0 : -1}
                    ref={inputRef}
                    {...rest}
                >
                    {buttonContent}
                </button>
            )}
            {tooltipValue && (
                <div className={styles.tooltip}>
                    {tooltipValue}
                </div>
            )}
        </div>
    )
}
